import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { Locale } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number for display with K, M, etc. suffixes
 * Examples: 1234 -> "1.2k", 1234567 -> "1.2m", 123 -> "123"
 * Returns "0" for null/undefined values
 */
export function formatNumber(num: number | null | undefined): string {
  if (num == null) {
    return "0";
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "m";
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  }
  return num.toString();
}

// ============================================================================
// CURRENCY CONVERSION & FORMATTING
// ============================================================================

// Map locale codes to their default currencies
const LOCALE_CURRENCY_MAP: Record<string, string> = {
  'en': 'USD',
  'en-US': 'USD',
  'en-GB': 'GBP',
  'en-AU': 'AUD',
  'en-CA': 'CAD',
  'es': 'EUR',
  'es-ES': 'EUR',
  'es-MX': 'MXN',
  'es-AR': 'ARS',
  'fr': 'EUR',
  'fr-FR': 'EUR',
  'fr-CA': 'CAD',
  'de': 'EUR',
  'de-DE': 'EUR',
  'de-AT': 'EUR',
  'de-CH': 'CHF',
  'it': 'EUR',
  'it-IT': 'EUR',
  'pt': 'EUR',
  'pt-PT': 'EUR',
  'pt-BR': 'BRL',
  'ja': 'JPY',
  'ja-JP': 'JPY',
  'ko': 'KRW',
  'ko-KR': 'KRW',
  'zh': 'CNY',
  'zh-CN': 'CNY',
  'zh-TW': 'TWD',
  'zh-HK': 'HKD',
  'ru': 'RUB',
  'ru-RU': 'RUB',
  'ar': 'SAR',
  'ar-SA': 'SAR',
  'ar-AE': 'AED',
  'hi': 'INR',
  'hi-IN': 'INR',
  'vi': 'VND',
  'vi-VN': 'VND',
};

// Exchange rates cache
interface ExchangeRateCache {
  rates: Record<string, number>;
  timestamp: number;
  baseCurrency: string;
}

let exchangeRateCache: ExchangeRateCache | null = null;
const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour cache
const EXCHANGE_RATE_API_URL = 'https://api.exchangerate-api.com/v4/latest/USD';

/**
 * Fetch exchange rates from API with caching
 */
async function fetchExchangeRates(): Promise<Record<string, number>> {
  // Return cached rates if still valid
  if (exchangeRateCache && Date.now() - exchangeRateCache.timestamp < CACHE_DURATION_MS) {
    return exchangeRateCache.rates;
  }

  // Check localStorage for persisted cache
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const cached = localStorage.getItem('gitfit_exchange_rates');
      if (cached) {
        const parsed = JSON.parse(cached) as ExchangeRateCache;
        if (Date.now() - parsed.timestamp < CACHE_DURATION_MS) {
          exchangeRateCache = parsed;
          return parsed.rates;
        }
      }
    } catch (e) {
      console.warn('[Currency] Failed to read cached rates:', e);
    }
  }

  try {
    const response = await fetch(EXCHANGE_RATE_API_URL);
    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }
    
    const data = await response.json();
    const rates = data.rates as Record<string, number>;
    
    // Cache in memory and localStorage
    exchangeRateCache = {
      rates,
      timestamp: Date.now(),
      baseCurrency: 'USD',
    };
    
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.setItem('gitfit_exchange_rates', JSON.stringify(exchangeRateCache));
      } catch (e) {
        console.warn('[Currency] Failed to persist rates:', e);
      }
    }
    
    return rates;
  } catch (error) {
    console.error('[Currency] Failed to fetch exchange rates:', error);
    // Return cached rates even if expired, or empty object
    return exchangeRateCache?.rates || {};
  }
}

/**
 * Get the user's preferred currency based on their locale
 */
export function getCurrencyForLocale(locale?: string): string {
  const effectiveLocale = locale || 
    (typeof window !== 'undefined' && window.localStorage?.getItem('i18nextLng')) ||
    (typeof navigator !== 'undefined' && navigator.language) ||
    'en-US';
  
  // Try exact match first, then base language code
  return LOCALE_CURRENCY_MAP[effectiveLocale] || 
         LOCALE_CURRENCY_MAP[effectiveLocale.split('-')[0]] || 
         'USD';
}

/**
 * Convert amount from USD to target currency
 * Returns the original amount if conversion fails
 */
export function convertCurrency(
  amountUSD: number,
  targetCurrency: string,
  rates: Record<string, number>
): number {
  if (targetCurrency === 'USD' || !rates[targetCurrency]) {
    return amountUSD;
  }
  return amountUSD * rates[targetCurrency];
}

/**
 * Initialize exchange rates - call this early in app startup
 * This pre-fetches rates so formatCurrency can use them synchronously
 */
export async function initializeExchangeRates(): Promise<void> {
  try {
    await fetchExchangeRates();
    console.log('[Currency] Exchange rates initialized');
  } catch (error) {
    console.warn('[Currency] Failed to initialize exchange rates:', error);
  }
}

/**
 * Format a number as currency with automatic conversion from USD
 * Uses the user's locale from i18n or browser settings
 * 
 * @param amountUSD - The numeric amount in USD
 * @param options - Optional configuration
 * @param options.skipConversion - If true, skip currency conversion (display in USD)
 * @param options.targetCurrency - Override the target currency
 * @param options.locale - Override the locale for formatting
 * @returns Formatted currency string in user's local currency
 */
export function formatCurrency(
  amountUSD: number | null | undefined,
  options?: {
    skipConversion?: boolean;
    targetCurrency?: string;
    locale?: string;
  }
): string {
  if (amountUSD == null) {
    amountUSD = 0;
  }
  
  const { skipConversion = false, targetCurrency, locale } = options || {};
  
  // Get locale from i18n or browser, fallback to 'en-US'
  const effectiveLocale = locale || 
    (typeof window !== 'undefined' && window.localStorage?.getItem('i18nextLng')) ||
    (typeof navigator !== 'undefined' && navigator.language) ||
    'en-US';
  
  // Determine target currency
  const currency = targetCurrency || (skipConversion ? 'USD' : getCurrencyForLocale(effectiveLocale));
  
  // Convert amount if needed
  let amount = amountUSD;
  if (!skipConversion && currency !== 'USD' && exchangeRateCache?.rates) {
    amount = convertCurrency(amountUSD, currency, exchangeRateCache.rates);
  }
  
  try {
    return new Intl.NumberFormat(effectiveLocale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    // Fallback to basic formatting if Intl fails
    console.warn('[formatCurrency] Intl.NumberFormat failed, using fallback:', error);
    return `$${amountUSD.toFixed(2)}`;
  }
}

/**
 * Format currency with guaranteed conversion (async version)
 * Use this when you need to ensure rates are fetched before formatting
 */
export async function formatCurrencyAsync(
  amountUSD: number | null | undefined,
  options?: {
    skipConversion?: boolean;
    targetCurrency?: string;
    locale?: string;
  }
): Promise<string> {
  // Ensure rates are fetched
  await fetchExchangeRates();
  return formatCurrency(amountUSD, options);
}

// ============================================================================
// METRIC NAME LOCALIZATION
// ============================================================================

/**
 * Map of built-in metric keys to their translation keys.
 * This provides a centralized mapping for all built-in metrics.
 */
const BUILTIN_METRIC_KEYS = [
  'calories',
  'protein', 
  'carbs',
  'fat',
  'fiber',
  'added_sugars',
  'water_gallons',
  'healthkit_steps',
  'healthkit_exercise_time',
  'healthkit_sleep_time',
  'healthkit_stand_hours',
  'healthkit_active_energy',
  'sleep',
  'body_weight',
  'body_fat_percentage',
  'gym_time',
  'miles_ran',
  'mood',
  'self_appearance_rating',
  'github_commits',
  'ate_healthy',
  'low_sugars',
  'fasted',
] as const;

export type BuiltinMetricKey = typeof BUILTIN_METRIC_KEYS[number];

/**
 * Check if a metric key is a built-in metric
 */
export function isBuiltinMetricKey(key: string): key is BuiltinMetricKey {
  return BUILTIN_METRIC_KEYS.includes(key as BuiltinMetricKey);
}

/**
 * Get the localized display name for a metric.
 * 
 * For built-in metrics, returns the translated name from `metric_names.<key>`.
 * For custom metrics, returns the provided fallback name.
 * 
 * @param key - The metric key (e.g., 'calories', 'protein', 'custom_1')
 * @param t - The i18n translation function
 * @param fallbackName - Optional fallback name to use if translation is not found
 * @returns The localized metric name
 */
export function getLocalizedMetricName(
  key: string,
  t: (key: string, options?: Record<string, unknown>) => string,
  fallbackName?: string
): string {
  // For built-in metrics, use the translation
  if (isBuiltinMetricKey(key)) {
    return t(`metric_names.${key}`, { defaultValue: fallbackName || key });
  }
  
  // For custom metrics or unknown keys, use the fallback
  return fallbackName || key;
}

/**
 * Get all built-in metric keys
 */
export function getBuiltinMetricKeys(): readonly string[] {
  return BUILTIN_METRIC_KEYS;
}

// ============================================================================
// DATE-FNS LOCALE SUPPORT
// ============================================================================

/**
 * Dynamically imports and returns the date-fns locale for the current language.
 * Returns undefined if the locale is not available (will use default English).
 */
export async function getDateFnsLocale(language?: string): Promise<Locale | undefined> {
  const effectiveLanguage = language || 
    (typeof window !== 'undefined' && window.localStorage?.getItem('i18nextLng')) ||
    'en';
  
  // date-fns locales export the locale object directly (not as default export)
  try {
    switch (effectiveLanguage) {
      case 'en':
        return (await import('date-fns/locale/en-US')).enUS;
      case 'es':
        return (await import('date-fns/locale/es')).es;
      case 'fr':
        return (await import('date-fns/locale/fr')).fr;
      case 'de':
        return (await import('date-fns/locale/de')).de;
      case 'zh':
        return (await import('date-fns/locale/zh-CN')).zhCN;
      case 'pt':
        return (await import('date-fns/locale/pt')).pt;
      case 'ar':
        return (await import('date-fns/locale/ar')).ar;
      case 'hi':
        return (await import('date-fns/locale/hi')).hi;
      case 'ja':
        return (await import('date-fns/locale/ja')).ja;
      case 'ko':
        return (await import('date-fns/locale/ko')).ko;
      case 'ru':
        return (await import('date-fns/locale/ru')).ru;
      case 'it':
        return (await import('date-fns/locale/it')).it;
      case 'vi':
        return (await import('date-fns/locale/vi')).vi;
      default:
        return undefined;
    }
  } catch (error) {
    console.warn(`[DateFns] Failed to load locale for ${effectiveLanguage}:`, error);
    return undefined;
  }
}